import React, { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Save, X, Upload, User } from "lucide-react";
import api from "@/lib/api";
import { fileToResizedDataUrl } from "@/lib/image";

const INPUT = "w-full px-3 py-2 rounded-lg border outline-none focus:ring-2 text-sm";
const BORDER = { borderColor: "#dcfce7" };
const emptyForm = { name: "", position: "", photo: "", order: 0 };

export default function OrgAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await api.get("/org-members");
    setItems(res.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm({ ...emptyForm, order: items.length }); setEditingId(null); setShowForm(true); };
  const startEdit = (m) => {
    setForm({ name: m.name, position: m.position, photo: m.photo || "", order: m.order });
    setEditingId(m.id);
    setShowForm(true);
  };

  const handleImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const dataUrl = await fileToResizedDataUrl(file, 600, 0.85);
    setForm((f) => ({ ...f, photo: dataUrl }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, order: parseInt(form.order, 10) || 0 };
      if (editingId) await api.put(`/org-members/${editingId}`, payload);
      else await api.post("/org-members", payload);
      setShowForm(false); setForm(emptyForm); setEditingId(null);
      await load();
    } catch (err) {
      alert("Gagal menyimpan data pengurus");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus anggota ini?")) return;
    await api.delete(`/org-members/${id}`);
    await load();
  };

  return (
    <div data-testid="admin-org">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-bold text-gray-900">Struktur Organisasi ({items.length})</h2>
        {!showForm && (
          <button onClick={startNew} data-testid="org-add-btn"
            className="inline-flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-semibold" style={{ backgroundColor: "#198530" }}>
            <Plus size={16} /> Tambah Anggota
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={save} data-testid="org-form" className="bg-white rounded-xl border p-5 mb-6 space-y-4" style={BORDER}>
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800">{editingId ? "Edit Anggota" : "Anggota Baru"}</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>
          <div className="flex items-center gap-4">
            {form.photo ? (
              <img src={form.photo} alt="preview" className="w-20 h-20 object-cover rounded-full border" style={BORDER} />
            ) : (
              <div className="w-20 h-20 rounded-full border flex items-center justify-center text-gray-300" style={BORDER}><User size={28} /></div>
            )}
            <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer" style={{ backgroundColor: "#eef9f0", color: "#198530" }}>
              <Upload size={16} /> Foto (opsional)
              <input type="file" accept="image/*" className="hidden" onChange={handleImage} data-testid="org-image" />
            </label>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Nama</label>
            <input className={INPUT} style={BORDER} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="org-name" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Jabatan</label>
              <input className={INPUT} style={BORDER} value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="org-position" required />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Urutan</label>
              <input type="number" className={INPUT} style={BORDER} value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="org-save"
              className="inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-lg text-sm font-bold disabled:opacity-60" style={{ backgroundColor: "#198530" }}>
              <Save size={16} /> {saving ? "Menyimpan..." : "Simpan"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="px-5 py-2.5 rounded-lg text-sm font-semibold border" style={BORDER}>Batal</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.map((m, idx) => (
          <div key={m.id} data-testid={`org-row-${m.id}`} className="bg-white rounded-xl border p-4 flex items-center gap-4" style={BORDER}>
            {m.photo ? (
              <img src={m.photo} alt={m.name} className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0" style={{ backgroundColor: "#198530" }}>{idx + 1}</div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 text-sm truncate">{m.name}</p>
              <p className="text-xs font-medium" style={{ color: "#198530" }}>{m.position}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => startEdit(m)} className="p-2 rounded-lg" style={{ backgroundColor: "#eef9f0", color: "#198530" }}><Edit2 size={16} /></button>
              <button onClick={() => remove(m.id)} data-testid={`org-delete-${m.id}`} className="p-2 rounded-lg bg-red-50 text-red-600"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full text-center text-gray-400 py-10">Belum ada data pengurus.</p>}
      </div>
    </div>
  );
}
